import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IonPage, IonContent, IonButton, IonIcon, IonSpinner } from "@ionic/react";
import { openOutline } from "ionicons/icons";
import { Browser } from "@capacitor/browser";
import AppLayout from "@/components/AppLayout";
import { SettingsCard, Rows, Muted } from "@/components/SettingsCards";
import { useSavedAssets } from "@/components/SavedAssets";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SITE_URL = (process.env.REACT_APP_PUBLIC_SITE_URL || "https://mintslip.com").replace(/\/$/, "");
const TIER_NAMES = { starter: "Express", express: "Express", professional: "Pro", pro: "Pro", business: "Enterprise", enterprise: "Enterprise" };
const STATUS = {
  active: ["Active", "admin-badge-green"], trialing: ["Trial", "admin-badge-amber"],
  cancelling: ["Cancelling", "admin-badge-amber"], past_due: ["Past due", "admin-badge-red"],
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// /app/billing in the iOS and Android app. The app sells nothing itself (the
// stores' in-app purchase rules), so instead of plans, payment methods,
// invoices and checkout this shows the plan and the balance, read only, and
// links out to the website's billing page. Coming back to the app refreshes
// both (AppLayout fires ms:entitlements-changed on resume).
export default function NativeBilling() {
  const navigate = useNavigate();
  const location = useLocation();
  const signedIn = isUserLoggedIn();
  // ?business=<id>: a plan for that business (a business just created)
  const forBusinessId = new URLSearchParams(location.search).get("business") || null;
  const { businesses } = useSavedAssets();
  const forBusiness = forBusinessId ? businesses.find((b) => b.id === forBusinessId) || null : null;
  const [status, setStatus] = useState(null);
  const [balance, setBalance] = useState(null);

  const load = useCallback(async () => {
    if (!isUserLoggedIn()) return;
    try {
      const d = await (await fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { headers: userHeaders() })).json();
      setStatus(d && d.success ? d : { hasSubscription: false });
    } catch { setStatus({ hasSubscription: false }); }
    try {
      const d = await (await fetch(`${BACKEND_URL}/api/wallet`, { headers: userHeaders() })).json();
      if (d.success) setBalance(d.balance || 0);
    } catch { /* the card shows without it */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useAppLive(load);
  useEffect(() => {
    window.addEventListener("ms:entitlements-changed", load);
    return () => window.removeEventListener("ms:entitlements-changed", load);
  }, [load]);

  const openSite = async () => {
    const q = new URLSearchParams({ from: "app" });
    if (forBusinessId) q.set("business", forBusinessId);
    const url = `${SITE_URL}/app/billing?${q.toString()}`;
    try { await Browser.open({ url }); }
    catch { try { window.open(url, "_blank"); } catch { /* nothing to do */ } }
  };

  const sub = status?.hasSubscription ? status : null;
  const tierName = sub ? (TIER_NAMES[sub.tier] || (sub.tier ? sub.tier.charAt(0).toUpperCase() + sub.tier.slice(1) : "Plan")) : null;
  const subStatus = sub ? (sub.cancelAtPeriodEnd ? "cancelling" : sub.status) : null;
  const [statusText, statusClass] = STATUS[subStatus] || [subStatus, "admin-badge-slate"];

  return (
    <AppLayout>
      <IonPage>
        <IonContent>
          <div className="admin-profile app-account" style={{ maxWidth: 720, margin: "0 auto", padding: "16px 16px 40px" }}>
            {!signedIn ? (
              <SettingsCard title="Billing">
                <Rows rows={[["Your plan and balance", "Sign in to see them. Plans and balance top-ups are on mintslip.com."]]} />
                <div style={{ padding: "4px 14px 14px" }}>
                  <IonButton expand="block" onClick={() => navigate("/login")}>Sign in</IonButton>
                </div>
              </SettingsCard>
            ) : status === null ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
            ) : (
              <>
                <SettingsCard title={forBusiness ? `Plan for ${forBusiness.name}` : "Your plan"}>
                  <Rows rows={forBusinessId ? [
                    ["Plan", forBusiness?.status && forBusiness.status !== "none" ? (TIER_NAMES[forBusiness.tier] || forBusiness.tier || "Plan") : <Muted>None yet</Muted>],
                  ] : [
                    ["Plan", sub ? tierName : <Muted>None</Muted>],
                    sub && ["Status", <span className={`admin-badge ${statusClass}`}>{statusText}</span>],
                    sub && ["Downloads", sub.downloadsRemaining === -1 ? "Unlimited" : `${sub.downloadsRemaining ?? 0} remaining`],
                    sub && sub.currentPeriodEnd && [subStatus === "cancelling" ? "Ends" : "Renews", new Date(sub.currentPeriodEnd).toLocaleDateString()],
                  ]} />
                </SettingsCard>
                {!forBusinessId && (
                  <SettingsCard title="Wallet Balance">
                    <Rows rows={[
                      ["Balance", balance === null ? <Muted>Loading</Muted> : money(balance)],
                      ["Used for", "Documents, once your downloads run out"],
                    ]} />
                  </SettingsCard>
                )}
                <IonButton expand="block" onClick={openSite} style={{ margin: "4px 0 10px" }}>
                  <IonIcon icon={openOutline} slot="start" /> Manage on mintslip.com
                </IonButton>
                <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--ion-color-medium)", textAlign: "center", lineHeight: 1.5 }}>
                  Plans, payment methods, invoices and your balance are managed on the MintSlip website. Changes show here when you come back to the app.
                </p>
              </>
            )}
          </div>
        </IonContent>
      </IonPage>
    </AppLayout>
  );
}
